// src/firebase/services.js
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  sendPasswordResetEmail
} from 'firebase/auth';
import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  orderBy,
  deleteDoc,
  updateDoc,
  setDoc,
  serverTimestamp
} from 'firebase/firestore';
import { auth, db, storage } from './config';
import { ref, uploadString, getDownloadURL, deleteObject } from 'firebase/storage';

// ── Auth ──────────────────────────────────────────────────────────────────────

export const registerUser = async (email, password, displayName) => {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  await updateProfile(cred.user, { displayName });
  return cred.user;
};

export const loginUser = (email, password) =>
  signInWithEmailAndPassword(auth, email, password);

export const logoutUser = () => signOut(auth);

export const resetPassword = (email) => sendPasswordResetEmail(auth, email);

// ── Certificate statuses ──────────────────────────────────────────────────────
// 'pending'  → submitted by user, awaiting admin review (NOT downloadable)
// 'approved' → reviewed and approved by admin (downloadable, shows PASS)
// 'rejected' → reviewed and rejected by admin (shows reason, not downloadable)

export const CERT_STATUS = {
  PENDING:  'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
};

// ── Certificates: create / read ──────────────────────────────────────────────

export const saveCertificate = async (userId, userEmail, certificateData) => {
  const ref = await addDoc(collection(db, 'certificates'), {
    ...certificateData,
    userId,
    userEmail: userEmail || '',
    createdAt: serverTimestamp(),
    status: CERT_STATUS.PENDING,
    reviewedAt: null,
    reviewedBy: null,
    rejectionReason: '',
    // Payment fields — populated in step 4 of the wizard
    certType: certificateData.certType || 'simple',
    certPrice: certificateData.certPrice || 0,
    paymentCrypto: certificateData.paymentCrypto || '',
    paymentTxHash: certificateData.paymentTxHash || '',
    paymentScreenshot: certificateData.paymentScreenshot || '', // Firebase Storage URL
    paymentSubmittedAt: serverTimestamp(),
  });
  return ref.id;
};

export const getUserCertificates = async (userId) => {
  const q = query(
    collection(db, 'certificates'),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getCertificate = async (certId) => {
  const snap = await getDoc(doc(db, 'certificates', certId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

// ── Public verification (no login required) ───────────────────────────────────
// Looks up a certificate by its human-readable certId (e.g. "COA-2026-0001"),
// NOT the Firestore document id. Only returns it if status === 'approved' —
// pending/rejected certificates are never exposed publicly.
export const getPublicCertificateByCertId = async (certId) => {
  const q = query(
    collection(db, 'certificates'),
    where('certId', '==', certId),
    where('status', '==', CERT_STATUS.APPROVED)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
};

export const deleteCertificate = (certId) =>
  deleteDoc(doc(db, 'certificates', certId));

export const updateCertificate = (certId, data) =>
  updateDoc(doc(db, 'certificates', certId), data);

// ── Certificates: admin review queue ──────────────────────────────────────────

// All certificates in 'pending' status, oldest first (FIFO review queue)
export const getPendingCertificates = async () => {
  const q = query(
    collection(db, 'certificates'),
    where('status', '==', CERT_STATUS.PENDING),
    orderBy('createdAt', 'asc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// All certificates regardless of status, newest first (admin "all" view)
export const getAllCertificates = async () => {
  const q = query(collection(db, 'certificates'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const approveCertificate = (certId, adminEmail, doctor = null, results = null, charts = [], richText = null, updatedCert = null) =>
  updateDoc(doc(db, 'certificates', certId), {
    status: CERT_STATUS.APPROVED,
    reviewedAt: serverTimestamp(),
    reviewedBy: adminEmail,
    rejectionReason: '',
    // Doctor who signed/approved the certificate
    doctorId:           doctor?.id           || '',
    doctorName:         doctor?.name         || '',
    doctorTitle:        doctor?.title        || '',
    doctorSignatureUrl: doctor?.signatureUrl || '',
    // Lab results filled by admin
    results: results || null,
    charts: charts || [],
    richText: richText || null,
    // Admin-edited client data (if modified in step 0)
    ...(updatedCert?.sample          ? { sample:          updatedCert.sample          } : {}),
    ...(updatedCert?.contact         ? { contact:         updatedCert.contact         } : {}),
    ...(updatedCert?.productLabelUrl ? { productLabelUrl: updatedCert.productLabelUrl } : {}),
  });

export const rejectCertificate = (certId, adminEmail, reason) =>
  updateDoc(doc(db, 'certificates', certId), {
    status: CERT_STATUS.REJECTED,
    reviewedAt: serverTimestamp(),
    reviewedBy: adminEmail,
    rejectionReason: reason || '',
  });

// Send a rejected certificate back to pending (after admin or user fixes something)
export const resubmitCertificate = (certId) =>
  updateDoc(doc(db, 'certificates', certId), {
    status: CERT_STATUS.PENDING,
    reviewedAt: null,
    reviewedBy: null,
    rejectionReason: '',
  });

// ── ID generator ──────────────────────────────────────────────────────────────

// ── Storage: payment screenshots ─────────────────────────────────────────────
// Uploads a base64 image to Firebase Storage and returns the public download URL.
// Path: screenshots/{certId}.jpg — one screenshot per certificate.
export const uploadPaymentScreenshot = async (certId, base64DataUrl) => {
  if (!base64DataUrl) return null;
  const screenshotRef = ref(storage, `screenshots/${certId}.jpg`);
  await uploadString(screenshotRef, base64DataUrl, 'data_url');
  const url = await getDownloadURL(screenshotRef);
  return url;
};

// Upload product label image — stored at labels/{certId}_{ts}.jpg
// Timestamp suffix ensures a fresh URL on every upload (avoids stale cache).
export const uploadProductLabel = async (certId, base64DataUrl) => {
  if (!base64DataUrl) return null;
  const ts = Date.now();
  const labelRef = ref(storage, `labels/${certId}_${ts}.jpg`);
  await uploadString(labelRef, base64DataUrl, 'data_url');
  const url = await getDownloadURL(labelRef);
  return url;
};

export const deletePaymentScreenshot = async (certId) => {
  try {
    const screenshotRef = ref(storage, `screenshots/${certId}.jpg`);
    await deleteObject(screenshotRef);
  } catch (e) { /* ignore if doesn't exist */ }
};

// ── Doctors ───────────────────────────────────────────────────────────────────
// Each doctor: { id, name, title, signatureUrl, createdAt }

export const getDoctors = async () => {
  const snap = await getDocs(collection(db, 'doctors'));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
};

export const saveDoctor = async (doctorData) => {
  const ref = await addDoc(collection(db, 'doctors'), {
    ...doctorData,
    createdAt: serverTimestamp(),
  });
  return ref.id;
};

export const updateDoctor = (doctorId, data) =>
  updateDoc(doc(db, 'doctors', doctorId), data);

export const deleteDoctor = (doctorId) =>
  deleteDoc(doc(db, 'doctors', doctorId));

export const uploadDoctorSignature = async (doctorId, base64DataUrl) => {
  if (!base64DataUrl) return null;
  const sigRef = ref(storage, `signatures/${doctorId}.png`);
  await uploadString(sigRef, base64DataUrl, 'data_url');
  return getDownloadURL(sigRef);
};

// ── Admin: User Management ────────────────────────────────────────────────────
// Create a new user (admin only) — uses Firebase Auth REST API
// because createUserWithEmailAndPassword logs out the current admin
export const adminCreateUser = async (name, email, password) => {
  const apiKey = auth.app.options.apiKey;
  const res = await fetch(
    `https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${apiKey}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, displayName: name, returnSecureToken: true }),
    }
  );
  const data = await res.json();
  if (data.error) throw new Error(data.error.message);
  // Store user profile in Firestore
  await setDoc(doc(db, 'users', data.localId), {
    uid: data.localId, name, email,
    createdAt: serverTimestamp(), role: 'user',
  });
  return { uid: data.localId, name, email };
};

// Get all users from Firestore users collection
export const getAllUsers = async () => {
  const snap = await getDocs(query(collection(db, 'users'), orderBy('createdAt', 'desc')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Duplicate a certificate and assign to a different user
export const duplicateCertificate = async (cert, targetUserId, targetUserEmail, targetUserName) => {
  const newCertId = generateCertificateId();

  // Update contact with target user's name — keep everything else identical
  const updatedContact = cert.contact ? {
    ...cert.contact,
    companyName: targetUserName || targetUserEmail || cert.contact.companyName,
  } : { companyName: targetUserName || targetUserEmail };

  const newCert = {
    // Copy ALL fields from original — nothing is cleared
    ...cert,
    // Override only identity + status fields
    certId:    newCertId,
    userId:    targetUserId,
    userEmail: targetUserEmail,
    status:    'pending',
    createdAt: serverTimestamp(),
    reviewedAt:      null,
    reviewedBy:      '',
    rejectionReason: '',
    // Updated contact with target user name
    contact: updatedContact,
    // Remove internal Firestore document id
    id: undefined,
  };
  delete newCert.id;
  const ref = await addDoc(collection(db, 'certificates'), newCert);
  return { id: ref.id, certId: newCertId };
};

export const generateCertificateId = () => {
  const year = new Date().getFullYear();
  const num = String(Math.floor(Math.random() * 9000) + 1000);
  return `COA-${year}-${num}`;
};
